import React, { useEffect, useMemo, useState } from 'react';
import { addBusinessDays, differenceInBusinessDays, format } from 'date-fns';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { StatCard } from '../../../../components/StatCard';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';

import {
  ActionsRow,
  Container,
  DeadlineAccent,
  DeadlineRow,
  Dot,
  FilterTab,
  FilterTabs,
  Header,
  NoDataContent,
  PageSubtitle,
  PageTitle,
  ReasonText,
  ReplyLink,
  RequestCard,
  RequestEmail,
  RequestHeader,
  RequestMeta,
  RequestsList,
  StatsRow,
  StatusBadge,
  StatusButton,
  TypeBadge,
} from './styles';

type Status = 'pending' | 'processed';
type RequestType = 'account' | 'data_only';

interface IAccountDeletionRequest {
  id: string;
  email: string;
  request_type: RequestType;
  reason: string | null;
  status: Status;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pendente',
  processed: 'Processada',
};

const REQUEST_TYPE_LABEL: Record<RequestType, string> = {
  account: 'Conta completa',
  data_only: 'Apenas dados',
};

const FILTERS: Array<{ key: Status | 'all'; label: string }> = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'processed', label: 'Processadas' },
  { key: 'all', label: 'Todas' },
];

// Mesmo prazo prometido no formulário público de exclusão
// (trenzox-landingpage `DeleteAccount`): "Concluímos a exclusão em até
// 15 dias úteis". Contado a partir da data da solicitação.
const DEADLINE_BUSINESS_DAYS = 15;

const AccountDeletionRequests: React.FC = () => {
  const { addToast } = useToast();

  const [requests, setRequests] = useState<IAccountDeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Status | 'all'>('pending');

  useEffect(() => {
    setLoading(true);
    api
      .get('account-deletion-requests')
      .then(response => setRequests(response.data))
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao buscar solicitações de exclusão.',
        }),
      )
      .finally(() => setLoading(false));
  }, [addToast]);

  const stats = useMemo(() => {
    const overdue = requests.filter(
      request =>
        request.status === 'pending' &&
        differenceInBusinessDays(
          addBusinessDays(new Date(request.created_at), DEADLINE_BUSINESS_DAYS),
          new Date(),
        ) < 0,
    ).length;

    return {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      overdue,
      processed: requests.filter(r => r.status === 'processed').length,
    };
  }, [requests]);

  // Mais urgente primeiro: pendentes (ordenadas da solicitação mais
  // antiga -- mais perto do prazo -- pra mais recente) e só depois as
  // já processadas.
  const sortedRequests = useMemo(
    () =>
      [...requests].sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === 'pending' ? -1 : 1;
        }

        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      }),
    [requests],
  );

  const filteredRequests = useMemo(
    () =>
      filter === 'all'
        ? sortedRequests
        : sortedRequests.filter(request => request.status === filter),
    [sortedRequests, filter],
  );

  const handleChangeStatus = async (id: string, status: Status) => {
    const previous = requests;

    setRequests(current =>
      current.map(request =>
        request.id === id
          ? {
              ...request,
              status,
              processed_at:
                status === 'processed' ? new Date().toISOString() : null,
            }
          : request,
      ),
    );

    try {
      await api.patch(`account-deletion-requests/${id}/status`, { status });
    } catch (err) {
      setRequests(previous);
      addToast({
        type: 'error',
        title: 'Erro ao atualizar status da solicitação.',
      });
    }
  };

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Exclusão de conta</PageTitle>
            <PageSubtitle>
              Solicitações enviadas pelo formulário público de exclusão de
              conta/dados
            </PageSubtitle>
          </div>
        </Header>

        <StatsRow>
          <StatCard title="Total" data={stats.total} />
          <StatCard title="Pendentes" data={stats.pending} />
          <StatCard title="Prazo vencido" data={stats.overdue} />
          <StatCard title="Processadas" data={stats.processed} />
        </StatsRow>

        <FilterTabs>
          {FILTERS.map(item => (
            <FilterTab
              key={item.key}
              type="button"
              active={filter === item.key}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </FilterTab>
          ))}
        </FilterTabs>

        {filteredRequests.length ? (
          <RequestsList>
            {filteredRequests.map(request => {
              const deadline = addBusinessDays(
                new Date(request.created_at),
                DEADLINE_BUSINESS_DAYS,
              );
              const businessDaysLeft = differenceInBusinessDays(
                deadline,
                new Date(),
              );

              let deadlineAccent: DeadlineAccent = 'normal';
              if (request.status === 'processed') {
                deadlineAccent = 'done';
              } else if (businessDaysLeft < 0) {
                deadlineAccent = 'danger';
              } else if (businessDaysLeft <= 3) {
                deadlineAccent = 'warning';
              }

              return (
                <RequestCard key={request.id}>
                  <RequestHeader>
                    <div>
                      <RequestEmail>{request.email}</RequestEmail>
                      <RequestMeta>
                        <TypeBadge>
                          {REQUEST_TYPE_LABEL[request.request_type]}
                        </TypeBadge>
                        <Dot />
                        <span>
                          Solicitado em{' '}
                          {format(new Date(request.created_at), 'dd/MM/yyyy')}
                        </span>
                      </RequestMeta>
                    </div>

                    <StatusBadge status={request.status}>
                      {STATUS_LABEL[request.status]}
                    </StatusBadge>
                  </RequestHeader>

                  <DeadlineRow accent={deadlineAccent}>
                    <Icon name="event" size={14} />
                    {request.status === 'processed' && request.processed_at
                      ? `Processada em ${format(
                          new Date(request.processed_at),
                          'dd/MM/yyyy',
                        )}`
                      : businessDaysLeft < 0
                      ? `Prazo vencido há ${Math.abs(
                          businessDaysLeft,
                        )} dia(s) útil(eis) -- prazo era ${format(
                          deadline,
                          'dd/MM/yyyy',
                        )}`
                      : `Prazo: ${format(
                          deadline,
                          'dd/MM/yyyy',
                        )} (${businessDaysLeft} dia(s) útil(eis) restante(s))`}
                  </DeadlineRow>

                  {!!request.reason && (
                    <ReasonText>{request.reason}</ReasonText>
                  )}

                  <ActionsRow>
                    {(['pending', 'processed'] as Status[]).map(status => (
                      <StatusButton
                        key={status}
                        type="button"
                        active={request.status === status}
                        disabled={request.status === status}
                        onClick={() => handleChangeStatus(request.id, status)}
                      >
                        {STATUS_LABEL[status]}
                      </StatusButton>
                    ))}

                    <ReplyLink
                      href={`mailto:${
                        request.email
                      }?subject=${encodeURIComponent(
                        'TrenzoX - Solicitação de exclusão de conta',
                      )}`}
                    >
                      <Icon name="reply" size={14} color={colors.primary} />
                      Falar com o solicitante
                    </ReplyLink>
                  </ActionsRow>
                </RequestCard>
              );
            })}
          </RequestsList>
        ) : (
          <NoDataContent>
            <Icon
              name="delete_forever"
              size={28}
              color={colors.onSurfaceVariant}
            />
            <span>Nenhuma solicitação por aqui ainda.</span>
          </NoDataContent>
        )}
      </Container>
    </Painel>
  );
};

export { AccountDeletionRequests };
